import React, { Fragment, useRef, useState } from 'react'
import Card from '../../../../../components/atoms/Card'
import Container from '../../../../../components/atoms/Container'
import Heading from '../../../../../components/atoms/Heading'
import Button from '../../../../../components/molecules/Button'
import { useTranslation } from '../../../../../providers/I18n'
import { BankCreditDetail } from '../../../../../types/credits'
import { StyledDescription } from '../../../../../components/styled/Description'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import {
  addCreditHistory,
  getCredit,
  updateCredit,
} from '../../../../../services/credits'
import { getCreditColor } from '../../../../../utils/credits'
import { useUser } from '../../../../../providers/User'
import CreditWaitAction from '../../../shared/CreditWaitAction'
import Textarea from '../../../../../components/atoms/Textarea'
import { CreditPhases, CreditSteps, Role } from '../../../../../types/enums'
import Spinner from '../../../../../components/atoms/Spinner'
import Input from '../../../../../components/atoms/Input'
import { useAlert } from '../../../../../providers/Alert'

const Phase2AppraisalClosed: React.FC = () => {
  const commentRef = useRef<HTMLTextAreaElement>(null)
  const unidadValuacionRef = useRef<HTMLInputElement>(null)
  const peritoRef = useRef<HTMLInputElement>(null)

  const { code } = useParams()
  const { user } = useUser()
  const { setAlert } = useAlert()
  const [loading, setLoading] = useState<boolean>(false)
  const { getTranslation } = useTranslation()
  const {
    data: credit,
    isLoading,
    refetch,
  } = useQuery<BankCreditDetail>(['getCredit', code], () => getCredit(code!), {
    refetchOnWindowFocus: false,
  })

  let header
  let footer
  let content
  if (!credit && isLoading) {
    content = (
      <Container>
        <StyledDescription>Cargando credito...</StyledDescription>
      </Container>
    )
  } else if (credit) {
    const isActiveOwner = user!.code === credit.owners.phase2Operator?.code
    const color = getCreditColor(credit.phase)
    const creditPhaseStep = `${getTranslation!(
      `phases.${credit.phase}.title`
    )} - ${getTranslation!(`phases.${credit.phase}.steps.${credit.step}`)}`

    header = (
      <Container padding>
        <Heading type="3" color={color}>
          {creditPhaseStep}
        </Heading>
      </Container>
    )

    if (isActiveOwner || user?.role !== Role.Advisor) {
      const handleSendToFormalizacion = async () => {
        setLoading(true)

        // const operator = await getNextOperator(CreditPhases.Phase3)
        const operator = credit.owners.phase2Operator._id

        const hasComment = !!commentRef.current?.value
        const hasPerito = !!peritoRef.current?.value
        const hasUnidadValuacion = !!unidadValuacionRef.current?.value
        if (!hasComment || !hasPerito || !hasUnidadValuacion) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            phase: CreditPhases.Phase3,
            step: CreditSteps.Phase3FormalizationRequest,
            meta: {
              appraisalPerito: peritoRef.current!.value,
              appraisalUnidadValuacion: unidadValuacionRef.current!.value,
            },
            owners: {
              phase3Operator: operator,
            },
          }).then(async () => {
            const comment = `Avanzando a Etapa 3 - Formalización :: ${commentRef.current?.value}`
            const creditHistoryComment = {
              comment: comment,
              type: 'status',
              start: new Date(),
              meta: {
                appraisalPerito: peritoRef.current!.value,
                appraisalUnidadValuacion: unidadValuacionRef.current!.value,
              },
              change: {
                from: {
                  owner: credit.owners.phase2Operator._id,
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  owner: credit.owners.advisor._id,
                  phase: CreditPhases.Phase3,
                  step: CreditSteps.Phase3FormalizationRequest,
                },
              },
            }
            await addCreditHistory(credit.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSendBack = async () => {
        setLoading(true)

        await updateCredit(credit!.code, {
          step: CreditSteps.Phase2AppraisalInProgress,
        }).then(async () => {
          const comment = `Regresando a Etapa 2 -En Avalúo :: ${commentRef.current?.value}`
          const creditHistoryComment = {
            comment: comment,
            type: 'status',
            start: new Date(),
            change: {
              from: {
                step: credit.step,
              },
              to: {
                step: CreditSteps.Phase2AppraisalInProgress,
              },
            },
          }

          await addCreditHistory(credit!.code, creditHistoryComment).then(
            () => {
              if (commentRef.current) {
                commentRef.current.value = ''
              }
              refetch().then(() => {
                setLoading(false)
              })
            }
          )
        })
      }

      content = (
        <Fragment>
          <StyledDescription>
            Ya se recibio el avalúo del banco, el tramite esta listo para
            formalización. Captura datos conforme avalúo y avanza expediente.
          </StyledDescription>
          <Input
            label="Unidad de Valuación"
            required
            ref={unidadValuacionRef}
          />
          <Input label="Perito" required ref={peritoRef} />
          <Textarea label="Comentarios" ref={commentRef} required />
        </Fragment>
      )

      footer = (
        <Container align="right" direction="row" padding>
          {loading ? (
            <Spinner type="bar" />
          ) : (
            <>
              {user?.role !== Role.Advisor && (
                <Button
                  color={color}
                  priority="secondary"
                  onClick={handleSendBack}
                >
                  Regresar a En Avalúo
                </Button>
              )}
              <Button onClick={handleSendToFormalizacion} color={color}>
                Mandar a Formalización
              </Button>
            </>
          )}
        </Container>
      )
    } else {
      content = <CreditWaitAction owner={credit.owners.phase2Operator} />
    }
  }

  return (
    <div>
      <Card header={header} footer={footer}>
        {content}
      </Card>
    </div>
  )
}

export default Phase2AppraisalClosed
